import { forwardRef, useContext, type CSSProperties, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import {
  SCROLLBAR_CLASS,
  SCROLLBAR_TRACK_BG_CLASS,
  SCROLLBAR_TRACK_BG_WHITE_CLASS,
  SCROLLBAR_TRACK_BORDER_CLASS,
} from '../../lib/scrollbar';
import { SLAYOUT_MIN_WIDTH_VAR, SLayoutContext } from '../SLayout/SLayout';
import type { SPageBackground } from './page.config';

/**
 * 스크롤 콘텐츠의 최소 폭 — SLayout 이 보장하는 레이아웃 최소 폭을 이 영역이 대신 삼킨다.
 *
 * 100cqw 는 SLayout 프레임의 폭, 100% 는 이 영역의 콘텐츠 폭이다.
 * "프레임이 최소 폭보다 좁은 만큼" 콘텐츠를 넓히므로, GNB 폭·접힘·header 구성이 어떻든
 * [GNB + 스크롤 끝까지 민 페이지] 합이 정확히 최소 폭이 된다. 패딩을 덮어써도 100% 가 따라온다.
 *
 * SLayout 밖이면 변수가 없어(폴백 0) 값이 음수가 되고, 음수 min-width 는 0 으로 클램프돼 규칙이 꺼진다.
 */
const MIN_CONTENT_WIDTH = `calc(var(${SLAYOUT_MIN_WIDTH_VAR}, 0px) - 100cqw + 100%)`;

/**
 * 배경별 표면색 + 스크롤바 처리. 구분선(bordered)과 트랙 배경은 셋 다 깔되, 트랙 색만 갈린다.
 *
 * 기본 트랙 색은 grey-10(#F6F6F6)인데 neutral 표면(bg.neutralLight)이 정확히 같은 색이라
 * 그대로 쓰면 트랙이 페이지에 묻힌다. 그래서 neutral 만 흰 트랙을 써서 홈을 드러낸다.
 * (트랙 배경을 아예 빼면 흰색이 아니라 transparent 라 페이지색이 비친다 — 묻히는 건 마찬가지다)
 */
const BACKGROUND_CLASS: Record<SPageBackground, string> = {
  frame: `bg-[var(--sys-color-bg-frame)] ${SCROLLBAR_TRACK_BORDER_CLASS} ${SCROLLBAR_TRACK_BG_CLASS}`,
  neutral: `bg-[var(--sys-color-bg-neutralLight)] ${SCROLLBAR_TRACK_BORDER_CLASS} ${SCROLLBAR_TRACK_BG_WHITE_CLASS}`,
  screen: `bg-[var(--sys-color-bg-screen)] ${SCROLLBAR_TRACK_BORDER_CLASS} ${SCROLLBAR_TRACK_BG_CLASS}`,
};

// docs:gen 은 export interface 만 수집하므로(gen-docs.mjs) 타입 별칭 대신 interface 로 둔다.
export interface SPageProps extends HTMLAttributes<HTMLElement> {
  /**
   * 페이지 배경 표면. frame=흰 콘텐츠 면, neutral=옅은 회색 면, screen=앱 바탕.
   * 스크롤바 처리도 여기 묶여 있다 — 셋 다 구분선+트랙 배경이고, 트랙 색만 neutral 에서 흰색이 된다.
   */
  background?: SPageBackground;
  /**
   * 스크롤 끝 여백. 마지막 항목이 창 하단에 붙어 "여기서 끝"이 안 읽히는 것을 막는다.
   * 기본으로 켜져 있고, 페이지네이션처럼 끝을 알려주는 것이 이미 있으면 끈다(목록 페이지).
   */
  scrollEndSpacing?: boolean;
}

/**
 * SPage — SLayout 안에서 남은 공간을 채우는 스크롤 콘텐츠 영역.
 * 스크롤은 SLayout(overflow:hidden)이 잡아둔 경계 안에서 이 영역에만 생긴다.
 * 창이 SLayout 의 최소 폭보다 좁을 때 생기는 가로 스크롤도 여기 걸린다(문서는 스크롤되지 않는다).
 * 패딩은 className 으로, 배경은 background 로 정한다(className 으로도 덮어쓸 수 있다).
 *
 * - header="fix": flex 자식으로 남은 가로 공간을 채운다.
 * - header="full": SLayout 이 깐 grid 의 "page" 영역 = 상단바 아래·메뉴 오른쪽 자리를 차지한다.
 *   좌표가 아니라 이름으로 잡으므로 프레임의 열·행 구성이 바뀌어도 여기는 그대로다.
 */
export const SPage = forwardRef<HTMLElement, SPageProps>(function SPage(
  { background = 'frame', scrollEndSpacing = true, className, style, children, ...rest },
  ref,
) {
  const layout = useContext(SLayoutContext);
  const gridStyle: CSSProperties | undefined =
    layout?.header === 'full' ? { gridArea: 'page' } : undefined;

  return (
    <main
      ref={ref}
      className={cn(
        'box-border min-h-0 min-w-0 flex-1 overflow-auto p-[var(--cmp-pageBody-padding-default)]',
        scrollEndSpacing && 'pb-[var(--cmp-pageBody-padding-scrollEnd)]',
        SCROLLBAR_CLASS,
        BACKGROUND_CLASS[background],
        className,
      )}
      style={{ ...gridStyle, ...style }}
      {...rest}
    >
      {/* 최소 폭을 삼키는 콘텐츠 상자. 넘치는 만큼 이 영역만 가로로 스크롤된다. */}
      <div style={{ minWidth: MIN_CONTENT_WIDTH }}>{children}</div>
    </main>
  );
});
